import { ReactNode, useMemo, useState } from "react";
import { Trans } from "react-i18next";
import i18nPackage from "../../i18n/i18n";
import CoreStrongText from "../text/Strong";
import { CoreButtonProps, CoreIconButton } from "../button/Button";
import CoreTextField from "../form/TextField";
import { CoreModal, CoreModalDescription, CoreModalProps } from "./Modal";
import styles from "./ConfirmationModal.module.css";
import CoreFormLabel from "../form/Label";
import { CoreCheckbox } from "../checkbox/Checkbox";

type ConfirmationStrategy =
	| {
			confirmationType: "text";
			confirmText?: string;
			checkboxLabel?: undefined;
	  }
	| {
			confirmationType: "checkbox";
			checkboxLabel?: ReactNode;
			confirmText?: undefined;
	  }
	| {
			confirmationType?: "none";
			confirmText?: undefined;
			checkboxLabel?: undefined;
	  };

export type CoreConfirmationModalProps = CoreModalProps & {
	onClose: Exclude<CoreModalProps["onClose"], undefined>;
	onConfirm: () => Promise<void> | void;
	slotProps?: {
		cancelButton?: Omit<CoreButtonProps, "onClick" | "children"> & {
			text?: ReactNode;
		};
		confirmButton?: Omit<CoreButtonProps, "onClick" | "disabled" | "children"> & {
			text?: ReactNode;
		};
	};
} & ConfirmationStrategy;

export default function CoreConfirmationModal({
	confirmationType = "none",
	onClose,
	onConfirm,
	confirmText,
	slotProps,
	children,
	checkboxLabel,
	...modalProps
}: CoreConfirmationModalProps) {
	const [inputText, setInputText] = useState("");
	const [checked, setChecked] = useState<boolean>(false);

	const canContinue = useMemo(() => {
		switch (confirmationType) {
			case "text": {
				const target = confirmText ?? i18nPackage.t("coreComponents.confirmationModal.iUnderstand");
				return inputText.trim() === target;
			}
			case "checkbox":
				return checked;
			case "none":
			default:
				return true;
		}
	}, [checked, confirmText, confirmationType, inputText]);

	return (
		<CoreModal
			width="720px"
			buttons={
				<>
					<CoreIconButton color="secondary" {...slotProps?.cancelButton} onClick={onClose}>
						{slotProps?.cancelButton?.text ?? i18nPackage.t("coreComponents.confirmationModal.cancel")}
					</CoreIconButton>
					<CoreIconButton
						{...slotProps?.confirmButton}
						disabled={!canContinue}
						onClick={async (event) => {
							await onConfirm();
							onClose(event);
						}}
					>
						{slotProps?.confirmButton?.text ?? i18nPackage.t("coreComponents.confirmationModal.confirm")}
					</CoreIconButton>
				</>
			}
			{...modalProps}
			onClose={onClose}
		>
			<div className={styles.modalBody}>
				{children}
				{confirmationType === "text" && (
					<div className={styles.textFieldArea}>
						<CoreModalDescription>
							<Trans
								i18nKey="coreComponents.confirmationModal.iUnderstandInstructions"
								components={{ b: <CoreStrongText color="dark" /> }}
							/>
						</CoreModalDescription>
						<CoreTextField
							className={styles.textField}
							value={inputText}
							onChange={(e) => setInputText(e.target.value)}
							label={i18nPackage.t("coreComponents.confirmationModal.iUnderstandPlaceholder")}
						/>
					</div>
				)}
				{confirmationType === "checkbox" && (
					<CoreFormLabel
						className={styles.checkboxFormLabel}
						labelPlacement="end"
						control={<CoreCheckbox checked={checked} onChange={(e) => setChecked(e.target.checked)} />}
					>
						{checkboxLabel ?? i18nPackage.t("coreComponents.confirmationModal.iUnderstand")}
					</CoreFormLabel>
				)}
			</div>
		</CoreModal>
	);
}
